import React, { useMemo, useState } from "react";
import { Box, Paper, Snackbar, Alert } from "@mui/material";
import { useParams } from "react-router-dom";
import { useAuthContext } from "../../../../context/AuthProvider";
import { useFetchServiceOrderById, useChangeServiceOrderState } from "../../../../hooks/administration/useServiceOrders";
import { useViajeros } from "../../../../hooks/administration/useServiceDetails";
import { useFetchRouteById } from "../../../../hooks/useRoutes";
import { useCopyTravelers } from "../../../../hooks/administration/useCopyTravelers";

import ServiceOrderHeaderCard from "./ServiceOrderHeaderCard";
import ServiceOrderSummaryCards from "./ServiceOrderSummaryCards";
import TravelersToolbar from "./TravelersToolbar";
import BulkImportTravelersDialog from "./BulkImportTravelersDialog";
import CopyTravelersDialog from "./CopyTravelersDialog";
import ConfirmDeleteDialog from "./ConfirmDeleteDialog";
import TravelerForm from "../TravelerForm";
import TravelersManifestReport from "./TravelersManifestReport";
import TravelersExcelExportButton from "./TravelersExcelExportButton";
import CollaboratorsModal from "./CollaboratorsModal";

const EDITABLE_STATES = ["CREADO", "VALIDADA", "LIBERADA", "EN_EJECUCION", "PRE_VALORIZADA"];

const ordenarPorApellido = (lista = []) =>
    [...lista].sort((a, b) =>
        `${a.perape || ""} ${a.pernom || ""}`.localeCompare(
            `${b.perape || ""} ${b.pernom || ""}`,
            "es",
            { sensitivity: "base" }
        )
    );

const normalizeTraveler = (t, isClosedReport = false) => {
    const estadoOrden = t.ordserdetestpas || t.estadoorden;
    const estadoReporteOriginal = t.estadoreporte;

    const estadoReporte =
        isClosedReport && estadoOrden === "NO_ASIGNADO"
            ? "NO_VIAJO"
            : estadoReporteOriginal;

    return {
        ...t,
        ordserdetestpas: estadoOrden,
        ordserdettra: t.ordserdettra || t.tramo,
        ordserdettaxser: t.ordserdettaxser ?? t.taximanifiesto ?? false,
        manid: t.manid || null,
        maninf: t.maninf || null,
        manest: t.manest || null,
        estadomanifiesto: t.estadomanifiesto || null,
        estadoreporte: estadoReporte,
    };
};

const CLOSED_REPORT_STATES = ["FINALIZADA", "VALORIZADA", "FACTURADA", "CERRADA"];

const ServiceOrderDetail = () => {
    const { serviceId } = useParams();
    const { user } = useAuthContext();
    const adminID = Number(process.env.REACT_APP_ROL_ADMIN_ID);
    const administradorID = Number(process.env.REACT_APP_ROL_ADMINISTRADOR_ID);
    const gerenciaID = Number(process.env.REACT_APP_ROL_GERENTE_ID);
    const clienteID = Number(process.env.REACT_APP_ROL_INVITADO_ID);

    const isClientUser = Number(user?.RolID) === clienteID;
    const canViewFinancialData = [adminID, administradorID, gerenciaID].includes(
        Number(user?.RolID)
    );
    const canEnableServiceTaxi = [administradorID, gerenciaID].includes(
        Number(user?.RolID)
    )

    const permissions = useMemo(() => ({
        isClientUser,
        canViewFinancialData,
        canEnableServiceTaxi,
        canEditTravelers: !isClientUser,
        canDeleteTravelers: !isClientUser,
        canCreateTravelers: !isClientUser,
        canCopyTravelers: !isClientUser,
        canBulkImportTravelers: !isClientUser,
        canChangeServiceState: !isClientUser,
        canViewInternalStatus: !isClientUser,
        canViewTaxi: !isClientUser,
        canViewActions: !isClientUser,
    }), [isClientUser, canViewFinancialData, canEnableServiceTaxi]);

    //const { changeStateMutation, useFetchService } = useServiceOrders();
    const changeStateMutation = useChangeServiceOrderState();
    const {
        data: service,
        isLoading,
        isError,
        error: serviceError,
    } = useFetchServiceOrderById(serviceId);    
    //const { data: service, isLoading, isError } = useFetchService(serviceId);

    const routeId = service?.rutid;
    const serviceForbidden = serviceError?.response?.status === 403;
    const { data: routeData } = useFetchRouteById(
        routeId,
        Boolean(routeId) && permissions.canEditTravelers && !serviceForbidden
    );

    const paradasActuales = routeData?.paradas || [];
    const shouldConvertNoAsignadoToNoViajo = CLOSED_REPORT_STATES.includes(service?.ordsercabest);
    const canLoadTravelers = Boolean(service?.ordsercabid) && !serviceForbidden;

    const {
        //viajeros: travelers = [],
        viajeros: rawTravelers = [],
        refetch: refetchTravelers,
        addMutation: addTraveler,
        updateMutation: updateTraveler,
        deleteMutation: deleteTraveler,
        isLoading: loadingTravelers,
        bulkImportMutation,
    } = useViajeros(serviceId, {
        enabled: canLoadTravelers,
    });

    const travelers = useMemo(
        () =>
            rawTravelers.map((t) =>
                normalizeTraveler(t, shouldConvertNoAsignadoToNoViajo)
            ),
        [rawTravelers, shouldConvertNoAsignadoToNoViajo]
    );

    //const canEditTravelers = EDITABLE_STATES.includes(service?.ordsercabest);
    const canEditTravelers = permissions.canEditTravelers && EDITABLE_STATES.includes(service?.ordsercabest);

    const [alert, setAlert] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const [openTravelerForm, setOpenTravelerForm] = useState(false);
    const [editTraveler, setEditTraveler] = useState(null);

    const [openBulkImport, setOpenBulkImport] = useState(false);
    const [openCopyModal, setOpenCopyModal] = useState(false);

    const [selectedTraveler, setSelectedTraveler] = useState(null);
    const [openConfirmDelete, setOpenConfirmDelete] = useState(false);

    const [openCollaboratorsModal, setOpenCollaboratorsModal] = useState(false);

    const [filters, setFilters] = useState({
        search: "",
        tramo: "TODOS",
        estado: "TODOS",
        taxi: "TODOS",
    });

    const showAlert = (message, severity = "success") => {
        setAlert({ open: true, message, severity });
    };

    const filteredTravelers = useMemo(() => {
        const search = filters.search.trim().toLowerCase();

        return travelers.filter((t) => {
            const fullName = `${t.pernom || ""} ${t.perape || ""}`.toLowerCase();
            const dni = `${t.perdni || ""}`;

            const matchSearch =
                !search || fullName.includes(search) || dni.includes(search);

            const matchTramo =
                filters.tramo === "TODOS" || t.ordserdettra === filters.tramo;

            const estadoOrden = t.ordserdetestpas || t.estadoorden;

            const matchEstado =
                filters.estado === "TODOS" || estadoOrden === filters.estado;

            const matchTaxi =
                filters.taxi === "TODOS" ||
                (filters.taxi === "SI" && !!t.ordserdettaxser) ||
                (filters.taxi === "NO" && !t.ordserdettaxser);

            return matchSearch && matchTramo && matchEstado && matchTaxi;
        });
    }, [travelers, filters]);

    const pasajerosIda = useMemo(
        () => ordenarPorApellido(filteredTravelers.filter((t) => t.ordserdettra === "IDA")),
        [filteredTravelers]
    );

    const pasajerosVuelta = useMemo(
        () => ordenarPorApellido(filteredTravelers.filter((t) => t.ordserdettra === "VUELTA")),
        [filteredTravelers]
    );

    const handleSubmitTraveler = (data) => {
        if (!permissions.canCreateTravelers && !permissions.canEditTravelers) return;
        if (editTraveler) {
            updateTraveler.mutate(
                {
                    servicioDetalleId: editTraveler.ordserdetid,
                    data: {
                        ...data,
                        updbyid: user.EmpID,
                        ordsercabid: serviceId,
                    },
                },
                {
                    onSuccess: () => {
                        showAlert("Viajero actualizado correctamente.");
                        setOpenTravelerForm(false);
                        setEditTraveler(null);
                        refetchTravelers();
                    },
                    onError: (error) => {
                        showAlert(
                            error?.response?.data?.error || "Error al actualizar el viajero.",
                            "error"
                        );
                    },
                }
            );
            return;
        }

        addTraveler.mutate(
            {
                ...data,
                ordsercabid: serviceId,
                crebyid: user.EmpID,
            },
            {
                onSuccess: () => {
                    showAlert("Viajero agregado correctamente.");
                    setOpenTravelerForm(false);
                    refetchTravelers();
                },
                onError: (error) => {
                    showAlert(
                        error?.response?.data?.error || "Error al agregar el viajero.",
                        "error"
                    );
                },
            }
        );
    };

    const handleOpenDelete = (traveler) => {
        if (!permissions.canDeleteTravelers) return;
        setSelectedTraveler(traveler);
        setOpenConfirmDelete(true);
    };

    const handleConfirmDelete = () => {
        if (!permissions.canDeleteTravelers || !selectedTraveler) return;
        if (!selectedTraveler) return;

        deleteTraveler.mutate(
            {
                servicioDetalleId: selectedTraveler.ordserdetid,
                data: {
                    perid: selectedTraveler.perid,
                    serviceId,
                },
            },
            {
                onSuccess: () => {
                    showAlert("Pasajero eliminado correctamente.");
                    setOpenConfirmDelete(false);
                    setSelectedTraveler(null);
                    refetchTravelers();
                },
                onError: (error) => {
                    showAlert(
                        error?.response?.data?.error || "Error al eliminar pasajero.",
                        "error"
                    );
                    setOpenConfirmDelete(false);
                },
            }
        );
    };

    const copyTravelers = useCopyTravelers(serviceId, () => {
        showAlert("Viajeros copiados correctamente.");
        refetchTravelers();
    });

    const handleChangeEstado = (id, newState) => {
        if (!permissions.canChangeServiceState) return;
        changeStateMutation.mutate(
            { id, newState },
            {
                onSuccess: () => showAlert("Estado actualizado correctamente."),
                onError: (error) =>
                    showAlert(error?.response?.data?.error || "Error al cambiar estado.", "error"),
            }
        );
    };

    if (serviceForbidden) {
        return (
            <Paper sx={{ p: 3 }}>
                <Alert severity="error">
                    No tienes permiso para visualizar esta orden de servicio o ya no se encuentra disponible para tu usuario.
                </Alert>
            </Paper>
        );
    }

    if (isLoading) {
        return <Paper sx={{ p: 3 }}>Cargando orden de servicio...</Paper>;
    }

    if (isError || !service) {
        return <Paper sx={{ p: 3 }}>No se pudo cargar la orden de servicio.</Paper>;
    }

    return (
        <Paper sx={{ p: 2 }}>
            <ServiceOrderHeaderCard
                service={service}
                serviceId={serviceId}
                user={user}
                onChangeEstado={permissions.canChangeServiceState ? handleChangeEstado : undefined}
                permissions={permissions}
                onManageCollaborators={() => setOpenCollaboratorsModal(true)}
            />

            <ServiceOrderSummaryCards
                service={service}
                travelers={travelers}
                user={user}
                permissions={permissions}
            />

            {!canEditTravelers && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Esta orden está en estado <strong>{service.ordsercabest}</strong>. La lista de
                    viajeros está bloqueada para edición.
                </Alert>
            )}

            <Box mt={3}>
                <TravelersToolbar
                    canEdit={canEditTravelers}
                    filters={filters}
                    setFilters={setFilters}
                    onAddTraveler={
                        permissions.canCreateTravelers
                            ? () => {
                                setEditTraveler(null);
                                setOpenTravelerForm(true);
                            }
                            : undefined
                    }
                    onOpenCopy={
                        permissions.canCopyTravelers
                            ? () => setOpenCopyModal(true)
                            : undefined
                    }
                    onOpenBulkImport={
                        permissions.canBulkImportTravelers
                            ? () => setOpenBulkImport(true)
                            : undefined
                    }
                    travelers={travelers}
                    permissions={permissions}
                />

                <Box display="flex" justifyContent="flex-end" mb={2}>
                    <TravelersExcelExportButton
                        travelers={filteredTravelers}
                        service={service}
                    />
                </Box>

                <TravelersManifestReport
                    title="SUBIDA"
                    travelers={pasajerosIda}
                    paradas={paradasActuales}
                    canEdit={canEditTravelers}
                    serviceId={serviceId}
                    user={user}
                    updateTraveler={updateTraveler}
                    onDelete={handleOpenDelete}
                    showAlert={showAlert}
                    refetchTravelers={refetchTravelers}
                    permissions={permissions}
                    service={service}
                />

                <TravelersManifestReport
                    title="BAJADA"
                    travelers={pasajerosVuelta}
                    paradas={paradasActuales}
                    canEdit={canEditTravelers}
                    serviceId={serviceId}
                    user={user}
                    updateTraveler={updateTraveler}
                    onDelete={handleOpenDelete}
                    showAlert={showAlert}
                    refetchTravelers={refetchTravelers}
                    permissions={permissions}
                    service={service}
                />
            </Box>

            {permissions.canCreateTravelers && openTravelerForm && (
                <TravelerForm
                    open={openTravelerForm}
                    handleClose={() => {
                        setOpenTravelerForm(false);
                        setEditTraveler(null);
                    }}
                    onSubmit={handleSubmitTraveler}
                    defaultValues={editTraveler || {}}
                    listTraveler={travelers || []}
                    ordenServicioId={serviceId}
                    paradas={paradasActuales}
                    service={service}
                />
            )}

            {permissions.canBulkImportTravelers && Number(service?.tipserid) !== 3 && (
                <BulkImportTravelersDialog
                    open={openBulkImport}
                    onClose={() => setOpenBulkImport(false)}
                    serviceId={serviceId}
                    user={user}
                    paradas={paradasActuales}
                    existingTravelers={travelers}
                    onSuccess={(result) => {
                        refetchTravelers();
                    }}
                    showAlert={showAlert}
                    bulkImportMutation={bulkImportMutation}
                />
            )}

            {permissions.canBulkImportTravelers && (
                <CopyTravelersDialog
                    open={openCopyModal}
                    onClose={() => setOpenCopyModal(false)}
                    copyTravelers={copyTravelers}
                    user={user}
                />
            )}

            <ConfirmDeleteDialog
                open={openConfirmDelete}
                traveler={selectedTraveler}
                onClose={() => {
                    setOpenConfirmDelete(false);
                    setSelectedTraveler(null);
                }}
                onConfirm={handleConfirmDelete}
                loading={deleteTraveler.isLoading}
            />

            {openCollaboratorsModal && (
                <CollaboratorsModal
                    open={openCollaboratorsModal}
                    handleClose={() => setOpenCollaboratorsModal(false)}
                    serviceId={serviceId}
                    showAlert={showAlert}
                />
            )}

            <Snackbar
                open={alert.open}
                autoHideDuration={4000}
                onClose={() => setAlert((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                <Alert severity={alert.severity} variant="filled">
                    {alert.message}
                </Alert>
            </Snackbar>
        </Paper>
    );
};

export default ServiceOrderDetail;